import { Component, Inject, OnInit, signal } from '@angular/core';
import {
    MatDialogRef,
    MAT_DIALOG_DATA,
    MatDialogTitle,
    MatDialogClose,
    MatDialogContent,
    MatDialogActions,
} from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

interface ColumnData {
    name: string;
    shouldDisplay: boolean;
    disabled?: boolean;
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-select-displayed-cols-dialog',
    templateUrl: './select-displayed-cols-dialog.component.html',
    styleUrls: ['./select-displayed-cols-dialog.component.scss'],
    imports: [
        MatDialogTitle,
        TranslateModule,
        MatIconButton,
        MatDialogClose,
        MatIcon,
        MatCheckbox,
        ReactiveFormsModule,
        FormsModule,
        MatDialogContent,
        MatDialogActions,
        MatButton,
    ],
})
export class SelectDisplayedColsDialogComponent implements OnInit {
    constructor(
        @Inject(MAT_DIALOG_DATA)
        private data: {
            allColumns: string[];
            displayedColumns?: string[];
            columnOrder?: string[];
            hiddenColumns?: string[];
            keyLabel?: string;
            isTours?: boolean;
            columnLabels?: Record<string, string>;
        },
        private dialogRef: MatDialogRef<SelectDisplayedColsDialogComponent>
    ) {}

    columnData = signal<ColumnData[]>([]);
    keyLabel?: string;
    isTours: boolean | undefined = false;
    columnLabels: Record<string, string> = {};
    useNewFormat = false;

    amountOfDisabled = 0;

    ngOnInit(): void {
        // Check if using new format (columnOrder + hiddenColumns)
        this.useNewFormat = !!this.data.columnOrder;
        const hiddenColumnsSet = new Set(this.data.hiddenColumns || []);

        // Use columnOrder if available, otherwise fall back to allColumns
        const columnsToIterate = this.data.columnOrder || this.data.allColumns;

        columnsToIterate.forEach(col => {
            let shouldDisplay = false;
            let disabled = false;
            if (
                col == 'details' ||
                col == 'checked' ||
                col == 'actions' ||
                col == 'select'
            ) {
                disabled = true;
                this.amountOfDisabled++;
            }

            if (this.useNewFormat) {
                shouldDisplay = !hiddenColumnsSet.has(col);
            } else if (this.data.displayedColumns) {
                shouldDisplay = this.data.displayedColumns.includes(col);
            }

            this.columnData.update(cols => [
                ...cols,
                {
                    name: col,
                    shouldDisplay,
                    disabled,
                },
            ]);
        });

        this.keyLabel = this.data.keyLabel;
        this.isTours = this.data.isTours;
        this.columnLabels = this.data.columnLabels ?? {};
    }

    // Prefer the column's own header translation key so the dialog label always
    // matches the table header, fall back to the key-based lookup
    getColumnLabel(name: string): string {
        return (
            this.columnLabels[name] || 'OUTGOING-GOODS.COLUMN-FILTERS.' + name
        );
    }

    get amountOfChecked(): number {
        return (
            this.columnData().filter(col => col.shouldDisplay).length -
            this.amountOfDisabled
        );
    }

    reset() {
        this.dialogRef.close({ reset: true });
    }

    closeDialog() {
        if (this.useNewFormat) {
            // Return hidden columns list for the new format
            const hiddenColumns = this.columnData()
                .filter(col => !col.shouldDisplay)
                .map(e => e.name);
            this.dialogRef.close({ hiddenColumns });
        } else {
            // Legacy format: return displayed columns array
            const colsToDisplay = this.columnData().filter(
                col => col.shouldDisplay
            );
            const cols = colsToDisplay.map(e => e.name);
            this.dialogRef.close(cols);
        }
    }
}
